import React, { useState, useEffect } from "react";
import http from "../../utils/axios";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Controlled as CodeMirror } from "react-codemirror2";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/dracula.css";
import "codemirror/mode/sql/sql";

interface PerformanceIndicator {
  id?: number;
  name: string;
  targetValue: number;
  unitOfMeasure: string;
  period: string;
  indicatorType: string;
  color: string;
  dashboardType: string;
  sqlQuery?: string;
}

interface PerformanceIndicatorFormProps {
  indicator: PerformanceIndicator | null;
  onClose: () => void;
}

const PerformanceIndicatorForm: React.FC<PerformanceIndicatorFormProps> = ({
  indicator,
  onClose,
}) => {
  const [formValues, setFormValues] = useState<PerformanceIndicator>({
    name: "",
    targetValue: 0,
    unitOfMeasure: "",
    period: "",
    indicatorType: "",
    color: "",
    dashboardType: "",
    sqlQuery: "",
  });
  useEffect(() => {
    const populateData = async () => {
      if (indicator) {
        setFormValues(indicator);
      }
    };
    populateData();
  }, [indicator]);
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleCodeChange = (editor: any, data: any, value: string) => {
    setFormValues({
      ...formValues,
      sqlQuery: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await http.post("/performanceIndicator", formValues);
      if (indicator?.id) {
        toast.success("Operação realizada com sucesso");
      } else {
        toast.success("Operação realizada com sucesso");
      }
      onClose();
    } catch (error) {
      toast.error("Ocorreu o seguinte erro ao realizar a operação: " + error);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{indicator?.id ? "Editar" : "Criar"}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            name="name"
            label="Name"
            value={formValues.name}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            name="targetValue"
            label="Target Value"
            type="number"
            value={formValues.targetValue}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            name="unitOfMeasure"
            label="Unit of Measure"
            value={formValues.unitOfMeasure}
            onChange={handleChange}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel id="period-label">Período</InputLabel>
            <Select
              labelId="period-label"
              name="period"
              value={formValues.period}
              onChange={handleChange}
            >
              <MenuItem value="Annually">Anual</MenuItem>
              <MenuItem value="Quarterly">Trimestral</MenuItem>
              <MenuItem value="Monthly">Mensal</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              type="color"
              name="color"
              label="Cor"
              value={formValues.color || ""}
              onChange={(e) => handleChange(e)}
              required
              fullWidth
            />
          </Box>

          <FormControl fullWidth required>
            <InputLabel id="dashboardType-label">Tipo de gráfico</InputLabel>
            <Select
              labelId="dashboardType-label"
              name="dashboardType"
              value={formValues.dashboardType}
              onChange={handleChange}
            >
              <MenuItem value="Gráfico de Barra">Gráfico de Barra</MenuItem>
              <MenuItem value="Gráfico de pizza">Gráfico de pizza</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="indicatorType-label">Tipo de Indicador</InputLabel>
            <Select
              labelId="indicatorType-label"
              name="indicatorType"
              value={formValues.indicatorType}
              onChange={handleChange}
            >
              <MenuItem value="OSS">OSS</MenuItem>
              <MenuItem value="Beneficiaries">Beneficiários</MenuItem>
            </Select>
          </FormControl>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <CodeMirror
              value={formValues.sqlQuery || ""}
              options={{
                mode: "sql",
                theme: "dracula", // Defina o tema para Dracula
                lineNumbers: true,
                lineWrapping: true,
              }}
              onBeforeChange={handleCodeChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          {indicator?.id ? "Update" : "Criar"}
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PerformanceIndicatorForm;
